import React, {
  useState,
  useRef,
  useImperativeHandle,
  forwardRef,
} from "react";
import { View, StyleSheet, Alert, Platform } from "react-native";
import type { TextInput as RNTextInput } from "react-native";
import { useTranslation } from "react-i18next";
import PhoneNumberComponent from "../../../common/steps/PhoneNumberComponent";
import { consumerLookup } from "../../../../../services/billpay.service";
import { SessionState, useSessionStore } from "../../../../../../../state/src/useSessionStore";

type Props = {
  onValidationChange?: (valid: boolean) => void;
};

export type EnterPhoneStepHandle = {
  submit: () => Promise<"ok" | "notfound">;
};

const EnterPhoneStep = forwardRef<EnterPhoneStepHandle, Props>(
  ({ onValidationChange }, ref) => {
    const { t } = useTranslation();
    const agentId = useSessionStore((s: SessionState) => s.agentId);
    const unitProfileId = useSessionStore((s: SessionState) => s.unitProfileId);
    const setPhoneNumberStore = useSessionStore(
      (s: SessionState) => (s as any).setPhoneNumber
    );
    const setIsNewConsumer = useSessionStore(
      (s: SessionState) => (s as any).setIsNewConsumer
    );

    const [phoneNumber, setPhoneNumber] = useState("");
    const inputRef = useRef<RNTextInput>(null);

    const formatPhoneNumber = (number: string) => {
      const cleaned = number.replace(/\D/g, "");
      if (cleaned.length <= 3) return cleaned;
      if (cleaned.length <= 6) return `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`;
      return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
    };

    const handlePhoneChange = (text: string) => {
      const cleaned = text.replace(/\D/g, "");
      if (cleaned.length <= 10) {
        setPhoneNumber(cleaned);
        setPhoneNumberStore?.(cleaned.length === 10 ? cleaned : null);
        onValidationChange?.(cleaned.length === 10);
      }
    };

    const submit = async (): Promise<"ok" | "notfound"> => {
      if (phoneNumber.length !== 10) {
        Alert.alert("Error", t("PHONE_STEP_ERROR_INVALID"));
        throw new Error("Invalid phone number");
      }
      if (!agentId || !unitProfileId) {
        Alert.alert("Error", t("PHONE_STEP_ERROR_MISSING_LOCATION"));
        throw new Error("Missing agent or unit profile");
      }
      try {
        await consumerLookup(phoneNumber, agentId, unitProfileId);
        setIsNewConsumer?.(false);
        return "ok";
      } catch (error: any) {
        const code = error?.code || error?.ErrorDetail?.ErrorCode;
        if (String(code) === "616") {
          setIsNewConsumer?.(true);
          return "notfound";
        }
        setIsNewConsumer?.(false);
        Alert.alert("Error", t("PHONE_STEP_ERROR_FAILED"));
        throw error;
      }
    };

    useImperativeHandle(ref, () => ({ submit }));

    const Wrapper = Platform.OS === "web" ? View : View;
    return (
      <Wrapper style={styles.container}>
        <PhoneNumberComponent
          title={t("PHONE_STEP_TITLE")}
          subTitle={t("PHONE_STEP_SUBTITLE")}
          inputRef={inputRef}
          phoneNumber={phoneNumber}
          formatPhoneNumber={formatPhoneNumber}
          handlePhoneChange={handlePhoneChange}
        />
      </Wrapper>
    );
  }
);

export default EnterPhoneStep;

const styles = StyleSheet.create({
  container: { backgroundColor: "#fff", flex: 1 },
});